import { fromTimeFormat } from "utils";
import { CourseTrainerPriceApi } from "api/types";
import { TRAINER_RATE_TYPE, TrainerRateType } from "api/trainer-costs/trainerCostsApi";
import { IDropdownOption } from "components/atoms";

export const MANUAL_RATE_OPTIONS = {
  HOURLY: "manual-hourly",
  DAILY: "manual-daily",
  TOPIC: "manual-topic",
} as const;

export const isManualRateSelection = (value: string): boolean =>
  (Object.values(MANUAL_RATE_OPTIONS) as string[]).includes(value);

export interface CalculationResult {
  quantity: number | null;
  total: number;
}

export const calculateCost = (
  scopePrices: CourseTrainerPriceApi[],
  rateType: TrainerRateType,
  rateAmount: number,
): CalculationResult => {
  if (rateType === TRAINER_RATE_TYPE.TOPIC) return { quantity: null, total: rateAmount };

  if (rateType === TRAINER_RATE_TYPE.HOURLY) {
    const hours = scopePrices.reduce(
      (acc, p) => acc + (fromTimeFormat(p.timeTo) - fromTimeFormat(p.timeFrom)) / 60,
      0,
    );
    return { quantity: hours, total: Math.round(rateAmount * hours * 100) / 100 };
  }

  const days = new Set(scopePrices.map((p) => new Date(p.date).toDateString())).size;
  return { quantity: days, total: rateAmount * days };
};

export interface RateSourceTrainer {
  prices: { name: string; amount: number; type: number }[];
}

export const buildRateOptions = (trainer?: RateSourceTrainer): IDropdownOption[] => [
  ...(trainer?.prices ?? []).map((p, idx) => ({ value: idx.toString(), label: `${p.name} (${p.amount} PLN)` })),
  { value: MANUAL_RATE_OPTIONS.HOURLY, label: "Ręczna stawka godzinowa" },
  { value: MANUAL_RATE_OPTIONS.DAILY, label: "Ręczna stawka dzienna" },
  { value: MANUAL_RATE_OPTIONS.TOPIC, label: "Ręczna kwota ryczałtowa" },
];

export const resolveRateType = (rateSelection: string, trainer?: RateSourceTrainer): TrainerRateType => {
  if (rateSelection === MANUAL_RATE_OPTIONS.HOURLY) return TRAINER_RATE_TYPE.HOURLY;
  if (rateSelection === MANUAL_RATE_OPTIONS.DAILY) return TRAINER_RATE_TYPE.DAILY;
  if (rateSelection === MANUAL_RATE_OPTIONS.TOPIC) return TRAINER_RATE_TYPE.TOPIC;
  return (trainer?.prices[Number(rateSelection)]?.type as TrainerRateType) ?? TRAINER_RATE_TYPE.TOPIC;
};
